import GIF from 'gif.js.optimized'
import workerStr from '@renderer/lib/gif.worker'
import { FilterFn, FrameConfig } from '@renderer/types/utils'
import QRCode from 'qrcode'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'
import cogoToast from '@successtar/cogo-toast'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import type { FilterAdjustments } from './filterUtils'
import type { Filter } from '@renderer/types/configState'
import { applyLUTThenOverlay, applyFilterOverlayImage} from './lutUtils'
import { getCachedImageUrl } from './imageCacheUtils'
import { LOSSLESS_ENCODING, reencodingFor, type ImageEncoding } from './captureEncoding'

const CREATE_FILE_RETRY_DELAYS_MS = [0, 300, 800]

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isTransientImageFetchFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true
  if (error instanceof Error) {
    const m = error.message
    if (m === 'Failed to fetch') return true
    if (/network/i.test(m)) return true
    if (m.includes('Load failed')) return true
  }
  return false
}

function isRetryableHttpErrorMessage(message: string): boolean {
  return /^HTTP (5\d\d|408|429)\b/.test(message)
}

/**
 * Smallest byte count we accept as a real MP4. A valid encode of even one frame is several
 * KB; anything under this is a failed encode. The specific value that shipped to guests was
 * 9 bytes — `new Blob([undefined])` stringifies to the literal text "undefined", so a
 * recorder that never encoded anything produced an "mp4" that uploaded and logged cleanly.
 */
const MP4_MIN_VALID_BYTES = 1024

/** Normalize whatever canvas-record's `stop()` returns into a validated video/mp4 Blob. */
function toMp4Blob(recorded: ArrayBuffer | Uint8Array | Blob[] | undefined): Blob {
  if (!recorded) {
    throw new Error('MP4 encoder returned no data')
  }
  const blob = Array.isArray(recorded)
    ? new Blob(recorded, { type: 'video/mp4' })
    : new Blob([recorded as BlobPart], { type: 'video/mp4' })
  if (blob.size < MP4_MIN_VALID_BYTES) {
    throw new Error(`MP4 encoder returned ${blob.size} bytes (no frames encoded)`)
  }
  return blob
}

class ImageUtils {
  private imageCache: Map<string, ImageBitmap> = new Map()
  /** MIME type of each image in `imageCache`, so a pass that rewrites it can keep its format. */
  private imageTypeCache: Map<string, string> = new Map()
  private stripCache: Map<string, Blob> = new Map()
  private qrCache: Map<string, ImageBitmap> = new Map()

  async processPreview(
    blob: Blob,
    options: {
      ratio?: number
      filter?: FilterFn
      /**
       * Explicit crop rectangle, normalized [0,1] in the source image's own pixel space.
       * When present it OVERRIDES the centered `ratio` crop — this is how the Bahelakotak
       * booth captures the exact off-center physical-box window each camera frames. The
       * source blob here is the full (uncropped, unrotated) sensor frame, so a normalized
       * rect maps straight onto it regardless of capture resolution.
       */
      cropRect?: { x: number; y: number; w: number; h: number }
      /** Output encoding. Defaults to PNG, the historical output of every caller. */
      encoding?: ImageEncoding
    }
  ): Promise<Blob> {
    const { ratio, filter, cropRect, encoding = LOSSLESS_ENCODING } = options
    const img = await createImageBitmap(blob)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!

    if (cropRect) {
      // Explicit off-center crop (kotak custom box).
      const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
      let sx = Math.round(clamp01(cropRect.x) * img.width)
      let sy = Math.round(clamp01(cropRect.y) * img.height)
      let sw = Math.max(1, Math.round(clamp01(cropRect.w) * img.width))
      let sh = Math.max(1, Math.round(clamp01(cropRect.h) * img.height))
      if (sx + sw > img.width) sx = Math.max(0, img.width - sw)
      if (sy + sh > img.height) sy = Math.max(0, img.height - sh)
      sw = Math.min(sw, img.width - sx)
      sh = Math.min(sh, img.height - sy)
      canvas.width = sw
      canvas.height = sh
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)
    } else {
      // Centered crop of the target aspect (default path for all other booths).
      const { width, height } = this.calculateDimensions(img, ratio)
      canvas.width = width
      canvas.height = height
      this.drawCroppedImage(ctx, img, width, height)
    }

    // Apply filter if provided
    if (filter) {
      filter(ctx)
    }

    const result = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob from canvas'))
          }
        },
        encoding.type,
        encoding.quality
      )
    })
    img.close()
    return result
  }

  /**
   * Returns the CSS scale transform needed to visually mirror a canvas whose
   * image data is **unrotated** but which has a CSS `rotate(Xdeg)` applied to it.
   * Because CSS rotation changes the visual axis orientation, the mirror axis must
   * be swapped for 90°/270°: scaleY(-1) produces a horizontal flip after a 90° CSS rotate.
   *
   * NOTE: This is only for CSS-based preview display. For pixel-level transforms on
   * saved images use `applyRotationMirroring` instead.
   */
  getRotationAwareMirrorTransform(rotation: number, mirror: boolean): string {
    if (!mirror) return ''

    const normalizedRotation = ((rotation % 360) + 360) % 360
    switch (normalizedRotation) {
      case 0:
      case 180:
        return 'scaleX(-1)'
      case 90:
      case 270:
        return 'scaleY(-1)'
      default:
        return ''
    }
  }

  private calculateDimensions(
    img: ImageBitmap,
    targetRatio?: number
  ): { width: number; height: number } {
    if (!targetRatio) return { width: img.width, height: img.height }

    let width, height
    const imageRatio = img.width / img.height

    if (targetRatio > imageRatio) {
      width = img.width
      height = img.width / targetRatio
    } else {
      height = img.height
      width = img.height * targetRatio
    }

    return { width, height }
  }

  private drawCroppedImage(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    img: ImageBitmap,
    targetWidth: number,
    targetHeight: number
  ): void {
    const x = (img.width - targetWidth) / 2
    const y = (img.height - targetHeight) / 2
    ctx.drawImage(img, x, y, targetWidth, targetHeight, 0, 0, targetWidth, targetHeight)
  }

  async loadImage(src: string, timeoutMs = 15000): Promise<ImageBitmap> {
    if (this.imageCache.has(src)) {
      return this.imageCache.get(src)!
    }

    // Get cached blob URL if available, otherwise use original URL
    const cachedUrl = await getCachedImageUrl(src)

    // Bound the fetch with an AbortController. A bare fetch() has no timeout, so a stalled
    // download (slow/offline booth network) can hang every caller indefinitely — this is what
    // froze the post-capture filter pass. data:/blob: URLs are local but share the same path.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    let blob: Blob
    try {
      const response = await fetch(cachedUrl, { signal: controller.signal })
      if (!response.ok) {
        throw new Error(`loadImage failed: HTTP ${response.status} for ${cachedUrl}`)
      }
      blob = await response.blob()
    } finally {
      clearTimeout(timer)
    }

    const imageBitmap = await createImageBitmap(blob)
    this.imageCache.set(src, imageBitmap)
    this.imageTypeCache.set(src, blob.type)
    return imageBitmap
  }

  async createFileFromUrl(url: string, fileName: string): Promise<File> {
    let lastError: unknown
    for (let attempt = 0; attempt < CREATE_FILE_RETRY_DELAYS_MS.length; attempt++) {
      const delay = CREATE_FILE_RETRY_DELAYS_MS[attempt]
      if (delay > 0) await sleepMs(delay)
      try {
        const cachedUrl = await getCachedImageUrl(url)
        const response = await fetch(cachedUrl)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const blob = await response.blob()
        return new File([blob], fileName, { type: blob.type || 'image/jpeg' })
      } catch (e) {
        lastError = e
        if (attempt === CREATE_FILE_RETRY_DELAYS_MS.length - 1) break
        const msg = e instanceof Error ? e.message : ''
        const retryable = isTransientImageFetchFailure(e) || isRetryableHttpErrorMessage(msg)
        if (!retryable) break
      }
    }
    throw lastError instanceof Error ? lastError : new Error(String(lastError))
  }

  async createStrip(
    frameConfig: FrameConfig,
    selectedPhotosUrls: string[],
    sessionId?: string,
    scaleFactor: number = 0.3
  ): Promise<string> {
    const cacheKey = JSON.stringify({ frameConfig, selectedPhotosUrls, sessionId, scaleFactor })
    if (this.stripCache.has(cacheKey)) {
      return URL.createObjectURL(this.stripCache.get(cacheKey)!)
    }

    const { drawParams } = frameConfig
    const scaledWidth = Math.round(drawParams.canvasWidth * scaleFactor)
    const scaledHeight = Math.round(drawParams.canvasHeight * scaleFactor)
    const canvas = new OffscreenCanvas(scaledWidth, scaledHeight)
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!

    ctx.scale(scaleFactor, scaleFactor)

    // Draw the base image
    const baseImage = await this.loadImage(frameConfig.baseImage)
    ctx.drawImage(baseImage, 0, 0, drawParams.canvasWidth, drawParams.canvasHeight)

    // Load and draw photos
    const loadedPhotos: { [key: string]: ImageBitmap } = {}
    for (const [index, photoUrl] of selectedPhotosUrls.entries()) {
      const photoType = `photo-${index + 1}`
      loadedPhotos[photoType] = await this.loadImage(photoUrl)
    }

    for (const imageParams of drawParams.images) {
      if (imageParams.type.startsWith('photo-')) {
        const photoIndex = parseInt(imageParams.type.split('-')[1])
        const photoType = `photo-${photoIndex}`
        if (loadedPhotos[photoType]) {
          ctx.drawImage(
            loadedPhotos[photoType],
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height
          )
        }
      } else if (imageParams.type === 'other') {
        if (imageParams.src) {
          const otherImage = await this.loadImage(imageParams.src)
          ctx.drawImage(
            otherImage,
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height
          )
        }
      }
    }

    // Draw QR code if sessionId is provided
    if (sessionId) {
      const qrImage = await this.generateQRCode(sessionId)
      const qrParams = drawParams.images.find((img) => img.type === 'qr')
      if (qrParams) {
        ctx.drawImage(qrImage, qrParams.left, qrParams.top, qrParams.width, qrParams.height)
      }
    }

    const blob = await canvas.convertToBlob({ type: 'image/png' })
    this.stripCache.set(cacheKey, blob)
    return URL.createObjectURL(blob)
  }

  /**
   * Apply watermark to a blob image
   */
  async applyWatermarkToBlob(
    imageBlob: Blob,
    watermarkUrl: string,
    watermarkHorizontalAnchor: 'left' | 'right',
    watermarkVerticalAnchor: 'top' | 'bottom',
    watermarkHorizontalOffset: number,
    watermarkVerticalOffset: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      const img = new Image()
      const watermark = new Image()

      let imagesLoaded = 0
      const checkComplete = (): void => {
        imagesLoaded++
        if (imagesLoaded === 2) {
          processImages()
        }
      }

      img.onload = checkComplete
      img.onerror = (): void => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(imageBlob)

      watermark.onload = checkComplete
      watermark.onerror = (): void => reject(new Error('Failed to load watermark'))
      watermark.crossOrigin = 'anonymous'
      watermark.src = watermarkUrl

      const processImages = (): void => {
        try {
          // Set canvas size to match the original image
          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight

          // Draw the original image
          ctx.drawImage(img, 0, 0)

          // Calculate watermark position based on anchor and offset
          const watermarkX =
            watermarkHorizontalAnchor === 'right'
              ? canvas.width - (watermarkHorizontalOffset / 100) * canvas.width
              : (watermarkHorizontalOffset / 100) * canvas.width

          const watermarkY =
            watermarkVerticalAnchor === 'bottom'
              ? canvas.height - (watermarkVerticalOffset / 100) * canvas.height
              : (watermarkVerticalOffset / 100) * canvas.height

          // Scale watermark to a reasonable size (e.g., 10% of canvas width, maintaining aspect ratio)
          const watermarkMaxWidth = canvas.width * 0.1
          const watermarkScale = Math.min(1, watermarkMaxWidth / watermark.naturalWidth)
          const watermarkWidth = watermark.naturalWidth * watermarkScale
          const watermarkHeight = watermark.naturalHeight * watermarkScale

          // Draw watermark with slight transparency
          ctx.globalAlpha = 0.8
          ctx.drawImage(
            watermark,
            watermarkX - watermarkWidth / 2, // Center on the calculated position
            watermarkY - watermarkHeight / 2,
            watermarkWidth,
            watermarkHeight
          )
          ctx.globalAlpha = 1.0

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(img.src)
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob from canvas'))
              }
            },
            'image/jpeg',
            0.95
          ) // High quality JPEG
        } catch (error) {
          URL.revokeObjectURL(img.src)
          reject(error)
        }
      }
    })
  }

  async generateQRCode(sessionId: string): Promise<ImageBitmap> {
    const galleryTarget = resolveGallerySessionUrlFromStore(sessionId)
    const cacheKey = `qr_${galleryTarget}`
    if (this.qrCache.has(cacheKey)) {
      return this.qrCache.get(cacheKey)!
    }

    const qrDataUrl = await QRCode.toDataURL(galleryTarget, {
      margin: 0,
      width: 300
    })

    // Convert QR data URL directly to ImageBitmap
    const response = await fetch(qrDataUrl)
    const blob = await response.blob()
    const imageBitmap = await createImageBitmap(blob)

    this.qrCache.set(cacheKey, imageBitmap)
    return imageBitmap
  }

  private loadImageElement(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous' // Important for CORS
      img.onload = (): void => resolve(img)
      img.onerror = reject
      img.src = url
    })
  }

  async createGIF(
    imageUrls: string[],
    frameDelay: number = 500,
    customGifFrameUrl?: string
  ): Promise<Blob> {
    try {
      // Load all images first
      const images = await Promise.all(imageUrls.map((url) => this.loadImageElement(url)))
      if (images.length === 0) throw new Error('No images provided for GIF creation')

      let customFrameImage: HTMLImageElement | undefined
      if (customGifFrameUrl && customGifFrameUrl !== '') {
        try {
          customFrameImage = await this.loadImageElement(customGifFrameUrl)
        } catch (error) {
          console.error('Error loading custom GIF frame:', error)
          // Assuming cogoToast is available globally or imported if needed
          cogoToast.warn('Could not load custom GIF frame. Creating GIF without it.')
        }
      }

      // Determine canvas size based on first image
      const firstImage = images[0]
      const MAX_DIMENSION = 800 // Maximum size for either width or height
      const imgRatio = firstImage.width / firstImage.height

      let canvasWidth: number
      let canvasHeight: number

      if (imgRatio > 1) {
        // Landscape orientation
        canvasWidth = Math.min(MAX_DIMENSION, firstImage.width)
        canvasHeight = canvasWidth / imgRatio
      } else {
        // Portrait or square orientation
        canvasHeight = Math.min(MAX_DIMENSION, firstImage.height)
        canvasWidth = canvasHeight * imgRatio
      }

      // Round dimensions to even numbers
      canvasWidth = Math.round(canvasWidth / 2) * 2
      canvasHeight = Math.round(canvasHeight / 2) * 2

      // Create worker blob with type application/javascript
      const workerBlob = new Blob([workerStr], {
        type: 'application/javascript'
      })

      // Create worker URL with explicit origin checks
      const workerUrl = URL.createObjectURL(workerBlob)

      const gif = new GIF({
        workers: 2,
        workerScript: workerUrl,
        quality: 1,
        width: canvasWidth,
        height: canvasHeight,
        background: '#fff'
      })

      // Add each image to the GIF
      images.forEach((img) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        canvas.width = canvasWidth
        canvas.height = canvasHeight

        if (ctx) {
          // Clear canvas and draw image
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)

          // Calculate dimensions to maintain aspect ratio
          const currentImgRatio = img.width / img.height
          let drawWidth = canvas.width
          let drawHeight = canvas.height
          let x = 0
          let y = 0

          if (currentImgRatio > imgRatio) {
            // Current image is wider than the first image
            drawHeight = canvas.width / currentImgRatio
            y = (canvas.height - drawHeight) / 2
          } else if (currentImgRatio < imgRatio) {
            // Current image is taller than the first image
            drawWidth = canvas.height * currentImgRatio
            x = (canvas.width - drawWidth) / 2
          }
          // If ratios are equal, use full canvas

          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Draw custom GIF frame on top if provided
          if (customFrameImage) {
            ctx.drawImage(customFrameImage, 0, 0, canvas.width, canvas.height)
          }

          gif.addFrame(canvas, { delay: frameDelay, copy: true })
        }
      })

      // Return promise that resolves with the GIF blob
      return new Promise((resolve, reject) => {
        gif.on('finished', (blob: Blob) => {
          // Clean up worker URL
          URL.revokeObjectURL(workerUrl)
          resolve(blob)
        })
        gif.on('error', (error: Error) => {
          // Clean up worker URL even on error
          URL.revokeObjectURL(workerUrl)
          reject(error)
        })
        gif.render()
      })
    } catch (error) {
      console.error('Error creating GIF:', error)
      throw error
    }
  }

  async createMP4FromImages(
    imageUrls: string[],
    frameDelay: number = 1000,
    customFrameUrl?: string
  ): Promise<Blob> {
    try {
      const images = await Promise.all(imageUrls.map((url) => this.loadImageElement(url)))
      if (images.length === 0) throw new Error('No images provided for MP4 creation')

      // Optional overlay frame
      let customFrameImage: HTMLImageElement | undefined
      if (customFrameUrl && customFrameUrl !== '') {
        try {
          customFrameImage = await this.loadImageElement(customFrameUrl)
        } catch (error) {
          console.error('Error loading custom MP4 frame:', error)
          cogoToast.warn('Could not load custom frame. Creating video without it.')
        }
      }

      // Match GIF sizing logic for consistency
      const firstImage = images[0]
      const MAX_DIMENSION = 800
      const imgRatio = firstImage.width / firstImage.height

      let canvasWidth: number
      let canvasHeight: number

      if (imgRatio > 1) {
        canvasWidth = Math.min(MAX_DIMENSION, firstImage.width)
        canvasHeight = canvasWidth / imgRatio
      } else {
        canvasHeight = Math.min(MAX_DIMENSION, firstImage.height)
        canvasWidth = canvasHeight * imgRatio
      }

      canvasWidth = Math.round(canvasWidth / 2) * 2
      canvasHeight = Math.round(canvasHeight / 2) * 2

      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Failed to get canvas context')

      const FPS = 30
      const framesPerImage = Math.max(1, Math.round((frameDelay / 1000) * FPS))

      const recorder = new Recorder(ctx as unknown as RenderingContext, {
        name: 'dreambooth',
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: canvasWidth,
          height: canvasHeight,
          bitrate: 2_000_000
        },
        duration: Infinity,
        download: false,
        frameRate: FPS
      })

      try {
        // `start()` is async (it awaits encoder init). Awaiting it with `initOnly` is what
        // makes the frame loop below deterministic: without the await, every `step()` can run
        // while the recorder is still Initializing, in which case `step()` silently falls
        // through to `stop()` and encodes nothing — `stop()` then returns `undefined` and the
        // caller ends up with a 9-byte "undefined" .mp4 (see `toMp4Blob`).
        await recorder.start({ initOnly: true })
        if (recorder.status !== RecorderStatus.Recording) {
          throw new Error('MP4 recorder failed to initialize (video encoder unavailable)')
        }

        for (const img of images) {
          // Draw white background then image scaled to fit
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, canvasWidth, canvasHeight)

          const currentImgRatio = img.width / img.height
          let drawWidth = canvasWidth
          let drawHeight = canvasHeight
          let x = 0
          let y = 0

          if (currentImgRatio > imgRatio) {
            drawHeight = canvasWidth / currentImgRatio
            y = (canvasHeight - drawHeight) / 2
          } else if (currentImgRatio < imgRatio) {
            drawWidth = canvasHeight * currentImgRatio
            x = (canvasWidth - drawWidth) / 2
          }

          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          if (customFrameImage) {
            ctx.drawImage(customFrameImage, 0, 0, canvasWidth, canvasHeight)
          }

          for (let i = 0; i < framesPerImage; i++) {
            // Wait for GPU to finish drawing
            await new Promise((resolve) => requestAnimationFrame(resolve))
            await recorder.step()
          }
        }

        const recordedChunks = await recorder.stop()
        return toMp4Blob(recordedChunks)
      } catch (recordingError) {
        // Clean up recorder if it was started
        try {
          await recorder.stop()
        } catch (stopError) {
          console.warn('Error stopping recorder during cleanup:', stopError)
        }
        throw recordingError
      }
    } catch (error) {
      console.error('Error creating MP4:', error)
      throw error
    }
  }

  async resize(
    blob: Blob,
    options: {
      maxWidth?: number
      maxHeight?: number
      quality?: number
      resizeQuality?: 'pixelated' | 'low' | 'medium' | 'high'
    }
  ): Promise<Blob> {
    const { maxWidth, maxHeight, quality, resizeQuality } = options

    const tempBitmap = await createImageBitmap(blob)
    const { width: originalWidth, height: originalHeight } = tempBitmap
    tempBitmap.close()

    const resizeOptions: ImageBitmapOptions = {}
    if (maxWidth && maxHeight && (originalWidth > maxWidth || originalHeight > maxHeight)) {
      const aspectRatio = originalWidth / originalHeight
      if (originalWidth / maxWidth > originalHeight / maxHeight) {
        resizeOptions.resizeWidth = maxWidth
        resizeOptions.resizeHeight = Math.round(maxWidth / aspectRatio)
      } else {
        resizeOptions.resizeWidth = Math.round(maxHeight * aspectRatio)
        resizeOptions.resizeHeight = maxHeight
      }
      resizeOptions.resizeQuality = resizeQuality || 'high'
    }

    const finalBitmap = await createImageBitmap(blob, resizeOptions)

    const canvas = new OffscreenCanvas(finalBitmap.width, finalBitmap.height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Failed to get canvas context')

    ctx.drawImage(finalBitmap, 0, 0)
    finalBitmap.close()

    return canvas.convertToBlob({
      type: 'image/jpeg',
      quality: quality || 1.0
    })
  }

  async applyRotationMirroring(
    blob: Blob,
    rotation: number,
    mirror: boolean,
    /** Output encoding. Defaults to PNG, the historical output of every caller. */
    encoding: ImageEncoding = LOSSLESS_ENCODING
  ): Promise<Blob> {
    const bitmap = await createImageBitmap(blob)
    // For 90/270° rotations, canvas dimensions must swap so the output is correctly sized
    let canvasWidth = bitmap.width
    let canvasHeight = bitmap.height
    if (rotation === 90 || rotation === 270) {
      canvasWidth = bitmap.height
      canvasHeight = bitmap.width
    }
    const canvas = new OffscreenCanvas(canvasWidth, canvasHeight)
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('Failed to get canvas context')

    // Mirror + rotation must match Preview: CSS uses translate(-50%,-50%) rotate(R) then
    // getRotationAwareMirrorTransform (scaleX at 0/180°, scaleY at 90/270°). Pixel path mirrors
    // that composition: 0/180 mirror then rotate; 90/270 rotate then scaleY in canvas space.
    const normalizedRotation = ((rotation % 360) + 360) % 360
    ctx.translate(canvasWidth / 2, canvasHeight / 2)
    if (normalizedRotation === 0 || normalizedRotation === 180) {
      if (mirror) ctx.scale(-1, 1)
      if (normalizedRotation !== 0) ctx.rotate((normalizedRotation * Math.PI) / 180)
    } else {
      // 90 or 270 — match CSS `rotate` then `scaleY(-1)` (Preview canvas transform order)
      ctx.rotate((normalizedRotation * Math.PI) / 180)
      if (mirror) ctx.scale(1, -1)
    }
    ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)

    bitmap.close()

    return canvas.convertToBlob({
      type: encoding.type,
      quality: encoding.quality
    })
  }

  async applyFilterToImage(
    imageUrl: string,
    filterOrAdjustments?: FilterAdjustments | Filter
  ): Promise<string> {
    try {
      const imageBitmap = await this.loadImage(imageUrl)
      // Keep the photo's capture format: a JPEG booth's filtered photo is what gets
      // uploaded and printed, and must not come back out of here as a 3 MB PNG.
      const encoding = reencodingFor(this.imageTypeCache.get(imageUrl))

      const canvas = document.createElement('canvas')
      canvas.width = imageBitmap.width
      canvas.height = imageBitmap.height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })

      if (!ctx) throw new Error('Failed to get canvas context')

      ctx.drawImage(imageBitmap, 0, 0)

      const isFilterObj = filterOrAdjustments && '_id' in filterOrAdjustments
      if (isFilterObj) {
        const filter = filterOrAdjustments as Filter
        if (filter.filterMode === 'lut') {
          applyLUTThenOverlay(ctx, filter)
        } else if (filter.adjustments) {
          const { filterUtils } = await import('./filterUtils')
          filterUtils.applyAdjustments(ctx, filter.adjustments)
          applyFilterOverlayImage(ctx, filter.overlay)
        }
      } else if (filterOrAdjustments) {
        const { filterUtils } = await import('./filterUtils')
        filterUtils.applyAdjustments(ctx, filterOrAdjustments as FilterAdjustments)
      }

      // Convert to blob URL
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(URL.createObjectURL(blob))
            } else {
              reject(new Error('Failed to create blob from canvas'))
            }
          },
          encoding.type,
          encoding.quality
        )
      })
    } catch (error) {
      console.error('Error applying filter to image:', error)
      return imageUrl // Return original if filter fails
    }
  }

  /**
   * applyFilterToImage, baked in a filter worker so the renderer keeps painting and
   * answering taps. On the slowest booths one photo takes 3 to 17 s, and on the main
   * thread the whole UI stops for that long.
   *
   * Same result: the worker draws the same decoded bitmap and runs the same passes
   * (filterBake.ts) with the LUT and overlay this thread has cached, then encodes with the
   * same `reencodingFor` settings. Whenever the worker cannot do it, this is
   * applyFilterToImage on the main thread.
   */
  async applyFilterToImageInWorker(
    imageUrl: string,
    filterOrAdjustments?: FilterAdjustments | Filter
  ): Promise<string> {
    let imageBitmap: ImageBitmap
    try {
      imageBitmap = await this.loadImage(imageUrl)
    } catch (error) {
      // What applyFilterToImage does when the photo cannot be read.
      console.error('Error applying filter to image:', error)
      return imageUrl
    }
    const encoding = reencodingFor(this.imageTypeCache.get(imageUrl))

    try {
      const { filterBakeWorker } = await import('./filterBakeWorker')
      const blob = await filterBakeWorker.bake(imageBitmap, filterOrAdjustments, encoding)
      if (blob) return URL.createObjectURL(blob)
    } catch (error) {
      console.warn('Filter worker unavailable; filtering on the main thread:', error)
    }
    // loadImage cached the bitmap above, so this does not fetch the photo again.
    return this.applyFilterToImage(imageUrl, filterOrAdjustments)
  }
}

const imageUtils = new ImageUtils()
export { imageUtils }
